/**
 * User Service
 * Handles user profile and permission-related API calls
 */

import type { User } from "@/features/auth/store/authStore"
import { analytics } from "@/lib/analytics"
import { ANALYTICS_EVENTS, ANALYTICS_PROPERTIES } from "@/lib/analytics/constants"
import { apiClient } from "@/lib/api-client"
import { setTokens } from "@/lib/token-manager"
import { type BackendUser, transformUserResponse } from "@/lib/user-transformer"
import type {
  CreateProfileRequest,
  PermissionSettings,
  UpdatePermissionsRequest,
  UpdateProfileRequest,
} from "../types/profile.types"

// Export types for convenience
export type {
  CreateProfileRequest,
  PermissionSettings,
  UpdatePermissionsRequest,
  UpdateProfileRequest,
}

/**
 * Get user profile
 */
export async function getProfile(): Promise<{ user: User }> {
  const response = await apiClient.get<{
    success: boolean
    data: { user: BackendUser }
    message: string
  }>("/api/profiles")
  const user = transformUserResponse(response.data.data.user)
  return { user }
}

/**
 * Create user profile
 */
export async function createProfile(data: CreateProfileRequest): Promise<{ user: User }> {
  // Transform the data to remove null values, converting them to undefined
  const transformedData = Object.fromEntries(
    Object.entries(data).map(([key, value]) => [key, value === null ? undefined : value]),
  ) as CreateProfileRequest

  const response = await apiClient.post<{
    success: boolean
    data: {
      user: BackendUser
      tokens?: {
        accessToken: string
        refreshToken: string
        accessTokenExpiresAt: string
        refreshTokenExpiresAt: string
      }
      deviceId?: string
    }
    message: string
  }>("/api/profiles", transformedData)

  const { user: backendUser, tokens, deviceId } = response.data.data

  // Store tokens if provided
  if (tokens && deviceId) {
    setTokens({
      accessToken: tokens.accessToken,
      refreshToken: tokens.refreshToken,
      deviceId,
    })
  }

  const user = transformUserResponse(backendUser)

  // Track profile creation
  analytics.identify(user.id, {
    $email: user.email,
    $phone: user.phoneNumber,
    name: user.name,
    username: user.username,
  })

  analytics.track(ANALYTICS_EVENTS.PROFILE_CREATED, {
    name: user.name,
    username: user.username,
  })

  return { user }
}

/**
 * Update user profile
 */
export async function updateProfile(data: UpdateProfileRequest): Promise<{ user: User }> {
  const response = await apiClient.put<{
    success: boolean
    data: { user: BackendUser }
    message: string
  }>("/api/profiles", data)
  const user = transformUserResponse(response.data.data.user)

  // Track profile update - update Mixpanel profile with any changed fields
  if (data.email || data.phoneNumber) {
    analytics.setUserProfile({
      ...(data.email && { $email: data.email }),
      ...(data.phoneNumber && { $phone: data.phoneNumber }),
      ...(data.name && { name: data.name }),
      ...(data.username && { username: data.username }),
    })
  }

  analytics.track(ANALYTICS_EVENTS.PROFILE_UPDATED, {
    [ANALYTICS_PROPERTIES.FIELDS_UPDATED]: Object.keys(data),
  })

  return { user }
}

/**
 * Update user permission settings (for existing settings)
 */
export async function updatePermissionSettings(
  data: UpdatePermissionsRequest,
): Promise<{ permissionSettings: PermissionSettings }> {
  const response = await apiClient.put<{
    success: boolean
    data: { permissionSettings: PermissionSettings }
    message: string
  }>("/api/users/user-settings", data)
  return { permissionSettings: response.data.data.permissionSettings }
}

/**
 * Create user permission settings (for onboarding/first-time setup)
 */
export async function createPermissionSettings(
  data: UpdatePermissionsRequest,
): Promise<{ permissionSettings: PermissionSettings }> {
  const response = await apiClient.post<{
    success: boolean
    data: { permissionSettings: PermissionSettings }
    message: string
  }>("/api/users/user-settings", data)
  return { permissionSettings: response.data.data.permissionSettings }
}

/**
 * Get other user's profile by ID
 * Used for viewing another user's profile
 */
export async function getOtherProfile(userId: string): Promise<{ user: User }> {
  const response = await apiClient.get<{
    success: boolean
    data: { user: BackendUser }
    message: string
  }>(`/api/profiles/${userId}`)
  const backendUser = response.data.data.user
  const user = transformUserResponse(backendUser)

  // Override visibility fields with computed boolean values from backend
  // The backend returns both original settings and computed access
  // For other profiles, we need the computed access (true/false)
  return {
    user: {
      ...user,
      practice_rituals_visibility_access:
        (backendUser as unknown as { practice_rituals_visibility_access?: boolean })
          .practice_rituals_visibility_access ?? true,
      teaching_rituals_visibility_access:
        (backendUser as unknown as { teaching_rituals_visibility_access?: boolean })
          .teaching_rituals_visibility_access ?? true,
      attended_events_visibility_access:
        (backendUser as unknown as { attended_events_visibility_access?: boolean })
          .attended_events_visibility_access ?? true,
      stats_visibility_access:
        (backendUser as unknown as { stats_visibility_access?: boolean }).stats_visibility_access ??
        true,
      coach_visibility_access:
        (backendUser as unknown as { coach_visibility_access?: boolean }).coach_visibility_access ??
        true,
    },
  }
}

/**
 * Search users with pagination
 * Used for adding users to communities, rituals, etc.
 */
export async function searchUsers(params: {
  search?: string
  page?: number
  limit?: number
  community_id?: string
  include_current_user?: boolean
}): Promise<{
  users: Array<{
    id: string
    email: string
    name: string | null
    username: string | null
    city: string | null
    profilePictureUrl: string | null
    bio: string | null
    profileVisibility: "public" | "private"
    createdAt: string
    profileMedia?: Array<{
      isCover: boolean
      upload?: {
        id: string
        filePath: string
      }
    }>
  }>
  total: number
  page: number
  totalPages: number
}> {
  const response = await apiClient.get<{
    success: boolean
    data: {
      users: Array<{
        id: string
        email: string
        name: string | null
        username: string | null
        city: string | null
        profilePictureUrl: string | null
        bio: string | null
        profileVisibility: "public" | "private"
        createdAt: string
        profileMedia?: Array<{
          isCover: boolean
          upload?: {
            id: string
            filePath: string
          }
        }>
      }>
      total: number
      page: number
      totalPages: number
    }
    message: string
  }>("/api/users/search", { params })

  return response.data.data
}

/**
 * Get all system spiritual traditions
 */
export async function getSpiritualTraditions(): Promise<
  Array<{
    id: string
    title: string
  }>
> {
  const response = await apiClient.get<{
    success: boolean
    data: Array<{ id: string; title: string }>
    message: string
  }>("/api/users/spiritual-traditions")
  return response.data.data
}

/**
 * Get all system ethnic backgrounds
 */
export async function getEthnicBackgrounds(): Promise<
  Array<{
    id: string
    title: string
  }>
> {
  const response = await apiClient.get<{
    success: boolean
    data: Array<{ id: string; title: string }>
    message: string
  }>("/api/users/ethnic-backgrounds")
  return response.data.data
}

/**
 * Get paginated intentions with is_checked flag for current user
 */
export async function getIntentions(params: {
  page: number
  limit: number
  user_id?: string
}): Promise<{
  intentions: Array<{
    id: string
    title: string
    description: string
    is_checked: boolean
  }>
  total: number
  page: number
  totalPages: number
}> {
  const response = await apiClient.get<{
    success: boolean
    data: {
      intentions: Array<{
        id: string
        title: string
        description: string
        is_checked: boolean
      }>
      total: number
      page: number
      totalPages: number
    }
    message: string
  }>("/api/profiles/intentions", { params: params as Record<string, unknown> })
  return response.data.data
}

/**
 * Toggle (add/remove) an intention for the authenticated user
 */
export async function toggleIntention(intentionId: string): Promise<{
  intention: {
    id: string
    title: string
    description: string
    is_checked: boolean
  }
}> {
  const response = await apiClient.post<{
    success: boolean
    data: {
      intention: {
        id: string
        title: string
        description: string
        is_checked: boolean
      }
    }
    message: string
  }>(`/api/intentions/${intentionId}/toggle`)
  return response.data.data
}

/**
 * Create a custom intention for the authenticated user
 */
export async function createCustomIntention(data: { name: string; description?: string }): Promise<{
  intention: {
    id: string
    name: string
    slug: string | null
    description: string
    is_checked: boolean
  }
}> {
  const response = await apiClient.post<{
    success: boolean
    data: {
      intention: {
        id: string
        name: string
        slug: string | null
        description: string
        is_checked: boolean
      }
    }
    message: string
  }>("/api/intentions", data)
  return response.data.data
}
